import axios, { AxiosResponse } from 'axios'
import { isPlanLapsedResponse } from '@/lib/planLapse'
import * as Sentry from '@sentry/react'
import { toast } from 'sonner'
import type { CampaignListItem, Campaign, CreateCampaignData, UpdateCampaignData, CampaignStats, CampaignStageProgress, SentEmailItem, CampaignMember, CampaignReview } from '@/types/campaign'
import type { ApproversResponse, MyApprovalRequest } from '@/types/approvals'
import type { EmailSignature } from '@/types/signature'
import type { EstimatorCatalog, EstimateRequestPayload, EstimateResponse } from '@/types/billing'
import type { SenderEmail, SenderEmailAddress, CreateSenderEmailData, DetectedProvider, InboxMessage, InboxStats, InboxSendersResponse, InboxContactsResponse, InboxMailbox, InboxMailboxUsersResponse, AutoReplyRule, CreateAutoReplyRuleData } from '@/types/senderEmail'
import type { WarmupAccount, ConnectWarmupData, UpdateWarmupApiKeyData, OAuthWarmupInitResult, OAuthWarmupPollResult } from '@/types/warmup'
import type { TemplateListItem, MailStructureTemplate, TemplateCreateRequest, TemplateUpdateRequest } from '@/types/template'
import type { WorkflowMember } from '@/types/workflow'
import type { LLMKeyTestResult } from '@/types/settings'
import type { FeedbackMineResponse, FeedbackSeenResponse, FeedbackSubmitResult } from '@/types/feedback'
import { getAccessToken, getRefreshToken, setAccessToken, setTokenPair, clearTokens } from './tokenStorage'

// Base API URL
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'

/**
 * Resolve a backend-served file path (e.g. "/uploads/branding/..") to a URL the
 * browser can load directly. In production the SPA and API run on different
 * origins, so a relative path would resolve against the static-site origin and
 * 404 (broken image). We derive the backend origin from API_BASE_URL by
 * stripping the trailing "/api". When API_BASE_URL is just "/api" (dev), the
 * origin is empty and the path stays relative — which the Vite proxy forwards.
 */
export function resolveFileUrl(path?: string | null): string {
  if (!path) return ''
  if (/^(https?:|data:|blob:)/i.test(path)) return path // already absolute
  const origin = API_BASE_URL.replace(/\/api\/?$/, '')
  return `${origin}${path.startsWith('/') ? '' : '/'}${path}`
}

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Types matching the Email Marketing backend
export interface User {
  id: number
  email: string
  username: string
  full_name: string
  is_active: boolean
  is_verified: boolean
  company_name?: string | null
  company_description?: string | null
  company_website?: string | null
  created_at: string
  auth_provider?: 'password' | 'google' | 'microsoft' | 'sso' | null
  can_change_password?: boolean
  role?: 'owner' | 'admin' | 'manager' | 'representative' | 'analyst'
  account_id?: number
  /** Product tour: null/undefined = never offered, then snoozed, skipped or completed. */
  tour_state?: 'snoozed' | 'skipped' | 'completed' | null
}

/** Response of POST /team/transfer-ownership (AB#22363). */
export interface TransferOwnershipResult {
  new_owner_id: number
  new_owner_email: string
  previous_owner_id: number
  /** What the outgoing owner's role became — 'admin'. */
  previous_owner_role: string
  entitlements_moved: number
  credit_balances_moved: number
}

export interface TeamMember {
  id: number
  email: string
  full_name: string
  role: 'owner' | 'admin' | 'manager' | 'representative' | 'analyst'
}

export interface Invite {
  id: number
  email: string
  role: 'owner' | 'admin' | 'manager' | 'representative' | 'analyst'
  status: 'pending' | 'accepted' | 'expired' | 'revoked'
  created_at?: string | null
  expires_at?: string | null
}

export interface InviteValidation {
  email: string
  role: string
  account_name: string
  // True when this email already has a Beevelope account. accept_invite()
  // re-parents (or merges) that existing account onto the inviting one and
  // never touches a password either way, so the frontend skips the
  // name/password form and offers a plain "Accept" when this is true.
  existing_account: boolean
}

export interface InviteEligibility {
  eligible: boolean
  plan_name: string | null
}

/** GET /team/seats — the Team page's seat picture (Momentum per-seat spec §5). */
export interface TeamSeatStatus {
  plan_name: string | null
  per_seat_billing: boolean
  paid_seats: number
  occupied_seats: number
  committed_seats: number
  pending_paid_seats: number | null
  min_seats: number
  max_seats: number | null
  seats_available: number
  can_buy_more: boolean
  at_plan_ceiling: boolean
  seat_price_cents: number | null
  annual_seat_price_cents: number | null
  billing_cycle: string | null
}

export interface AssignableMember {
  id: number
  email: string
  full_name: string
  role: string
}

export interface AssignableMembersResponse {
  plan_name: string | null
  dropdown_eligible: boolean
  members: AssignableMember[]
}

export interface PendingApproval {
  workflow_id: number
  workflow_name: string
  requested_by_user_id: number
  requested_by_name: string
  requested_by_email: string
  requested_at: string | null
  // Approval record fields (2026-10-01): whom the Rep addressed the request to.
  approval_id?: number | null
  approver_id?: number | null
  approver_name?: string | null
  assigned_to_me?: boolean
}

/** One row of the campaign launch-approval queue. Carries audience size and
 *  send time on top of the workflow shape — an approver is signing off on a
 *  SEND, so "to how many, and when" is the decision. */
export interface PendingCampaignApproval {
  campaign_id: number
  campaign_name: string
  workflow_name: string
  audience_size: number
  scheduled_start: string | null
  requested_by_user_id: number
  requested_by_name: string
  requested_by_email: string
  requested_at: string | null
  approval_id?: number | null
  approver_id?: number | null
  approver_name?: string | null
  assigned_to_me?: boolean
  // Not yet active when the Rep's campaign parked its workflow with it;
  // approving the campaign also activates the workflow (brief C4).
  workflow_status?: string | null
}

export interface AuditLogEntry {
  id: number
  actor_user_id: number
  actor_role: 'owner' | 'admin' | 'manager' | 'representative' | 'analyst'
  action: string
  target_type?: string | null
  target_id?: string | null
  details?: unknown
  ip_address?: string | null
  result: 'success' | 'denied'
  created_at?: string | null
}

export interface LoginCredentials {
  email: string
  password: string
}

export interface RegisterData {
  email: string
  username: string
  full_name: string
  password: string
}

export interface AuthResponse {
  access_token: string
  refresh_token: string
  token_type: string
  user: User
}

export interface RegisterResponse {
  message: string
  email: string
}

export interface OtpStatusResponse {
  expires_in_seconds: number
  resend_in_seconds: number
}

export interface ApiError {
  detail: string
  status_code: number
}

/** One entry in FastAPI/pydantic's HTTP 422 validation-error array. */
interface FastApiValidationError {
  type?: string
  loc?: Array<string | number>
  msg?: string
}

// Field-name fragments that read better as an initialism than title-cased.
const FIELD_ACRONYMS: Record<string, string> = {
  api: 'API', id: 'ID', url: 'URL', smtp: 'SMTP', llm: 'LLM',
  ip: 'IP', sso: 'SSO', crm: 'CRM', utm: 'UTM',
}

/** "body", "target_daily_limit" -> "Target daily limit"; "body", "api_key" -> "API key". */
function humanizeFieldPath(loc: Array<string | number> = []): string {
  const words = loc
    .filter((p) => p !== 'body' && p !== 'query' && p !== 'path' && typeof p !== 'number')
    .flatMap((p) => String(p).split('_'))
  return words
    .map((w, i) => FIELD_ACRONYMS[w.toLowerCase()] || (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w.toLowerCase()))
    .join(' ')
}

// Common pydantic v2 message templates, rewritten into plain English. Order
// matters: more specific patterns first. Anything not matched here is passed
// through as-is (already-readable pydantic copy) rather than left blank.
const MESSAGE_REWRITES: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
  [/^field required$/i, () => 'is required'],
  [/^input should be less than or equal to (.+)$/i, (m) => `must be ${m[1]} or less`],
  [/^input should be greater than or equal to (.+)$/i, (m) => `must be at least ${m[1]}`],
  [/^input should be less than (.+)$/i, (m) => `must be less than ${m[1]}`],
  [/^input should be greater than (.+)$/i, (m) => `must be greater than ${m[1]}`],
  [/^string should have at least (\d+) characters?$/i, (m) => `must be at least ${m[1]} characters`],
  [/^string should have at most (\d+) characters?$/i, (m) => `must be at most ${m[1]} characters`],
  [/^input should be a valid (.+)$/i, (m) => `must be a valid ${m[1]}`],
  [/^value is not a valid (.+)$/i, (m) => `must be a valid ${m[1]}`],
]

function humanizeMessage(msg: string): string {
  for (const [pattern, rewrite] of MESSAGE_REWRITES) {
    const m = msg.match(pattern)
    if (m) return rewrite(m)
  }
  return msg
}

function capitalizeFirst(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

/** One FastAPI validation-error entry -> a plain-English sentence fragment. */
function formatValidationError(e: FastApiValidationError): string {
  const field = humanizeFieldPath(e.loc)
  const message = humanizeMessage(e.msg || 'is invalid')
  return capitalizeFirst(field ? `${field} ${message}` : message)
}

/**
 * Extract a user-friendly error message from an Axios error or unknown thrown value.
 * Use in catch blocks instead of `(error: any)`.
 */
export function getErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: string | FastApiValidationError[] })?.detail
    // FastAPI/pydantic returns HTTP 422 validation failures as an ARRAY of
    // {type, loc, msg} objects (e.g. loc: ["body", "target_daily_limit"], msg:
    // "Input should be less than or equal to 2000"), not a string. Passing
    // that raw array to toast.error() renders plain objects as React
    // children, which throws — and since <Toaster /> sits outside the app's
    // <ErrorBoundary>, that crash used to take down the whole page instead of
    // showing an error toast. Flatten it into a plain-English sentence
    // instead of surfacing the raw field name / pydantic wording.
    if (Array.isArray(detail)) {
      if (detail.length === 0) return fallback
      return detail.map(formatValidationError).join('; ')
    }
    return detail || error.message || fallback
  }
  if (error instanceof Error) {
    return error.message
  }
  return fallback
}

// Token management
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token!)
    }
  })

  failedQueue = []
}

/**
 * True when the caller already put an Authorization header on the request.
 *
 * In axios 1.x `config.headers` inside an interceptor is an `AxiosHeaders`
 * instance (case-insensitive `has()`), but tests and hand-built configs pass a
 * plain object, so both shapes are handled.
 */
export function hasExplicitAuthHeader(headers: unknown): boolean {
  if (!headers || typeof headers !== 'object') return false
  const h = headers as { has?: (name: string) => boolean; Authorization?: unknown; authorization?: unknown }
  if (typeof h.has === 'function') {
    try {
      return Boolean(h.has('Authorization'))
    } catch {
      // fall through to the plain-object check
    }
  }
  return Boolean(h.Authorization ?? h.authorization)
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Never clobber an Authorization header the caller set deliberately. The
    // refresh call below authenticates with the REFRESH token that way, and
    // overwriting it with the access token from storage meant /auth/refresh-token
    // always received the very token that had just expired — so the refresh
    // 401'd, the response interceptor read that as "session is over", cleared
    // storage and hard-navigated to /login. That was the fixed 24h logout
    // (== JWT_ACCESS_TOKEN_EXPIRE_MINUTES), not the refresh token's 7 days.
    if (hasExplicitAuthHeader(config.headers)) {
      return config
    }
    const token = getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Request was aborted by the caller (e.g. a Stop button) — not a failure.
    // Skip the toast/refresh/Sentry handling and let the caller deal with it.
    if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
      return Promise.reject(error)
    }

    // The backend refuses every credit spend once the plan has expired. Refresh
    // plan status so the UI flips into its lapsed state (banner, disabled
    // controls) now, not at the next reload. Dynamic import: authStore imports
    // this module, so a static one would be circular. Fire and forget.
    if (isPlanLapsedResponse(error.response?.status, error.response?.data?.detail)) {
      void import('@/stores/authStore').then((m) => m.useAuthStore.getState().fetchPlanStatus())
    }

    // A 401 from /auth/login or /auth/refresh-token means bad credentials or an
    // invalid refresh token — NOT an expired access token. Running the refresh
    // branch here would clear storage and hard-reload to /login, making a plain
    // "wrong password" response look like a bounced session.
    const requestUrl: string = originalRequest?.url || ''
    const isAuthEndpoint =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/refresh-token') ||
      requestUrl.includes('/auth/register') ||
      requestUrl.includes('/auth/logout')

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`
          return api(originalRequest)
        }).catch((err) => {
          return Promise.reject(err)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = getRefreshToken()

      if (!refreshToken) {
        // Reset the flag on this path too — it sits outside the try/finally
        // below, so leaving it set wedges `isRefreshing` at true and every
        // later 401 queues behind a refresh that will never run. It only ever
        // went unnoticed because this branch also hard-navigates away, which
        // throws the module state out with the page.
        isRefreshing = false
        processQueue(error, null)
        clearTokens()
        window.location.href = '/login'
        return Promise.reject(error)
      }

      try {
        const response = await api.post('/auth/refresh-token', {}, {
          headers: { Authorization: `Bearer ${refreshToken}` },
          _retry: true,  // Skip interceptor for this request to prevent deadlock
        } as any)

        const { access_token, refresh_token: rotatedRefreshToken } = response.data

        // The backend rotates the refresh token so an active session never
        // expires. Tolerate a deploy where the frontend is ahead of the API
        // and only an access token comes back.
        if (rotatedRefreshToken) {
          setTokenPair(access_token, rotatedRefreshToken)
        } else {
          setAccessToken(access_token)
        }

        processQueue(null, access_token)
        originalRequest.headers.Authorization = `Bearer ${access_token}`

        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        // Only a definitive rejection from the server means the session is
        // actually over. A network blip, an offline tab, a 502 from a
        // restarting backend — those leave perfectly valid tokens in storage,
        // and evicting the user to /login over one costs them any unsaved work
        // in the tab. Reject this request and let the next one try again.
        const refreshStatus = axios.isAxiosError(refreshError)
          ? refreshError.response?.status
          : undefined
        if (refreshStatus === 401 || refreshStatus === 403) {
          clearTokens()
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // Handle other errors
    if (error.response?.data?.detail) {
      // Don't show toast for auth errors as they're handled by components.
      // Requests sent with { skipErrorToast: true } own their error UI
      // (e.g. contact deletion shows a styled warning instead of a red toast).
      if (error.response.status !== 401 && !(originalRequest as any)?.skipErrorToast) {
        const detail = error.response.data.detail
        toast.error(typeof detail === 'string' ? detail : 'Request failed')
      }
    } else if (error.message && !error.response) {
      toast.error(error.message)
    }

    // Report failed API calls to Sentry so every API error in the UI is visible.
    // Server errors (5xx) and network failures are captured as events; all other
    // non-2xx responses are recorded as breadcrumbs so they show up in the
    // timeline of any later error without flooding Sentry with 4xx events.
    try {
      const status: number | undefined = error.response?.status
      const method = (originalRequest?.method || 'get').toUpperCase()
      const url: string = originalRequest?.url || ''
      const isServerError = status !== undefined && status >= 500
      const isNetworkError = !error.response
      if (isServerError || isNetworkError) {
        Sentry.captureException(error, {
          tags: { kind: 'api_error', http_status: status ?? 'network' },
          extra: {
            method,
            url,
            status,
            detail: error.response?.data?.detail,
          },
        })
      } else if (status !== undefined) {
        Sentry.addBreadcrumb({
          category: 'api',
          type: 'http',
          level: status >= 400 ? 'warning' : 'info',
          message: `${method} ${url} -> ${status}`,
          data: { method, url, status },
        })
      }
    } catch {
      // Never let telemetry break the request flow.
    }

    return Promise.reject(error)
  }
)

// One cell of the Settings > Roles & Access table (GET /auth/role-matrix) --
// a role's overall access to one feature area. `icon` is a display key
// ('check' | 'eye' | 'clock' | 'dash') resolved by roleAccessDisplay.ts;
// `note` is only present for an area with a gated (approval-required) action.
export interface RoleAccessCell {
  level: string
  label: string
  icon: string
  note?: string
}

export interface RoleAccessArea {
  key: string
  label: string
  access: Record<string, RoleAccessCell>
  // Present only when this area ALSO requires a specific subscription plan
  // on top of whatever role-based access the table shows (e.g. Team invites
  // are Momentum/Enterprise-only regardless of role).
  plan_note?: string
}

export interface RoleAccessMatrix {
  roles: string[]
  areas: RoleAccessArea[]
}

// GET /auth/plan-status — whether the caller's account is on the free trial
// and, if so, its numeric caps. Every role can call this (unlike
// billingApi.getSubscription, gated to billing.manage_subscription, which
// 403s for Manager/Rep/Analyst) — it's what powers trial-aware UI
// (cap banners, friendly blocked messages) everywhere, not just Billing.
export interface PlanStatusLimits {
  contacts: number
  workflows: number
  campaigns: number
  materials: number
}

/** A workflow node type the account's plan may not use. */
export interface GatedNode {
  /** ReactFlow node `type`, e.g. 'abTest'. */
  type: string
  /** The plan_gating.PLAN_FEATURES key that unlocks it. */
  feature: string
  /** True when only SOME configurations of this node need the feature — its
   *  free mode stays available, so lock the option and not the node
   *  (generateDeck's attach-a-file mode, internalAlert's email channel). */
  partial: boolean
}

export interface PlanStatus {
  plan_name: string | null
  is_trial: boolean
  limits: PlanStatusLimits | null
  /** Feature keys this account's plan unlocks (plan_gating.PLAN_FEATURES).
   *  Drives the in-app locks — a locked node, a locked provider, a locked
   *  settings section. UX only: every gate is re-checked server-side. */
  features: string[]
  /** Node types this plan may NOT use. Sent by the API rather than mirrored
   *  in TypeScript so the node→feature map lives in one place. */
  gated_nodes: GatedNode[]
  /** Integration ids this plan includes, from the same plan row the Billing
   *  cards render from (AB#22375). `null` means the plan carries no list,
   *  which the server treats as no restriction — the UI must match, or a
   *  seeding gap would lock every provider. */
  allowed_integrations: string[] | null
}

// Auth API endpoints (now correctly formatted for Email Marketing backend)
export const authApi = {
  login: (credentials: LoginCredentials): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/auth/login', credentials),

  register: (data: RegisterData): Promise<AxiosResponse<RegisterResponse>> =>
    api.post('/auth/register', data),

  logout: (): Promise<AxiosResponse<void>> =>
    api.delete('/auth/logout'),

  // Returns a rotated PAIR — persist both via setTokenPair(), not just the
  // access token. The explicit Authorization header carries the refresh token
  // and is left alone by the request interceptor (hasExplicitAuthHeader).
  refreshToken: (refreshToken: string): Promise<AxiosResponse<{ access_token: string; refresh_token?: string }>> =>
    api.post('/auth/refresh-token', {}, {
      headers: { Authorization: `Bearer ${refreshToken}` }
    }),

  getMe: (): Promise<AxiosResponse<User>> =>
    api.get('/auth/me'),

  /** Record what the user did with the product tour; returns the updated user. */
  updateTourState: (action: 'complete' | 'skip' | 'snooze'): Promise<AxiosResponse<User>> =>
    api.post('/auth/me/tour', { action }),

  // Resolved permission levels for the current user's role, e.g.
  // {"workflows.activate": "gated"}. Powers usePermission(); UX-only,
  // never the real security boundary (that's require_permission server-side).
  getPermissions: (): Promise<AxiosResponse<Record<string, string>>> =>
    api.get('/auth/permissions'),

  // Human-readable role x feature-area matrix for ALL 5 roles (not just the
  // caller's own) -- powers Settings > Roles & Access.
  getRoleMatrix: (): Promise<AxiosResponse<RoleAccessMatrix>> =>
    api.get('/auth/role-matrix'),

  getPlanStatus: (): Promise<AxiosResponse<PlanStatus>> =>
    api.get('/auth/plan-status'),

  updateProfile: (data: Partial<User>): Promise<AxiosResponse<User>> =>
    api.put('/auth/me', data),

  changePassword: (data: { current_password: string; new_password: string }): Promise<AxiosResponse<void>> =>
    api.put('/auth/change-password', data),

  // Forgot/reset password. forgotPassword always resolves with a generic
  // message (no account enumeration); resetPassword consumes the emailed token.
  forgotPassword: (data: { email: string }): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/auth/forgot-password', data),

  resetPassword: (data: { token: string; new_password: string }): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/auth/reset-password', data),

  verifyOtp: (data: { email: string; code: string }): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/auth/verify-otp', data),

  resendOtp: (data: { email: string }): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/auth/resend-otp', data),

  // Remaining seconds on the signup code and its resend cooldown, owned by the
  // backend so a refresh doesn't restart the countdown. Fetch once, count down
  // locally — don't poll.
  otpStatus: (email: string): Promise<AxiosResponse<OtpStatusResponse>> =>
    api.get('/auth/otp-status', { params: { email } }),

  // Start a WorkOS-brokered social ("google"|"microsoft") or enterprise SSO
  // login. For social, returns { authorization_url } to navigate to. For
  // mode=sso, returns { authorization_url } when the domain has a verified/active
  // SSO connection, otherwise { sso_available:false, domain } so the caller can
  // prompt setup or fall back. `redirect` is round-tripped via the signed state.
  ssoAuthorize: (
    params: { provider?: 'google' | 'microsoft'; mode?: 'sso' | 'work'; email?: string; redirect?: string }
  ): Promise<AxiosResponse<{ authorization_url?: string; sso_available?: boolean; domain?: string }>> =>
    api.get('/auth/sso/authorize', { params }),

  // Whether the signed-in user's company domain already has active SSO.
  ssoStatus: (): Promise<AxiosResponse<{ enabled: boolean; eligible?: boolean; configured?: boolean; domain?: string }>> =>
    api.get('/auth/sso/status'),

  // Self-serve enterprise SSO setup. Provisions the WorkOS org for the user's
  // company domain and returns an Admin Portal link where the org admin
  // configures their IdP and verifies domain ownership.
  ssoSetup: (): Promise<AxiosResponse<{ portal_url: string; domain: string; organization_id: string }>> =>
    api.post('/auth/sso/setup'),

  getAccountSettings: (): Promise<AxiosResponse<{ company_name?: string; company_website?: string; company_address?: string; company_phone?: string; company_logo_url?: string; brand_colors?: Record<string, string>; brand_book_url?: string; brand_guidelines_text_set?: boolean }>> =>
    api.get('/auth/account/settings'),

  updateAccountSettings: (data: { company_name?: string; company_website?: string; company_address?: string; company_phone?: string; brand_colors?: Record<string, string> }): Promise<AxiosResponse<any>> =>
    api.put('/auth/account/settings', data),

  uploadCompanyLogo: (file: File): Promise<AxiosResponse<{ logo_url: string }>> => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/auth/account/upload-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  },

  uploadBrandBook: (file: File): Promise<AxiosResponse<{ brand_book_url: string; filename: string }>> => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/auth/account/upload-brand-book', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
  },

  deleteCompanyLogo: (): Promise<AxiosResponse<any>> =>
    api.delete('/auth/account/brand-logo'),

  deleteBrandBook: (): Promise<AxiosResponse<any>> =>
    api.delete('/auth/account/brand-book'),
}

// Workflows API endpoints
export const workflowsApi = {
  // `userId` is the "created by" team filter — accepted only for roles with
  // account-wide workflow scope; the API 403s an own-scoped caller naming
  // anyone else. Omitted entirely when unset so the call shape stays
  // `(skip, limit)` for every existing caller.
  getAll: (skip = 0, limit = 50, userId?: number): Promise<AxiosResponse<any[]>> =>
    api.get('/workflows/', {
      params: userId != null ? { skip, limit, user_id: userId } : { skip, limit },
    }),

  getMembers: (): Promise<AxiosResponse<WorkflowMember[]>> =>
    api.get('/workflows/members'),

  // `config` is for call sites that own their error UI: CampaignWorkflowFlow
  // passes skipErrorToast because a campaign can legitimately reference a
  // workflow the viewer cannot open (one returned to a member who left), and
  // a raw "Workflow not found" toast over a campaign page reads as a bug.
  getById: (id: number, config?: any): Promise<AxiosResponse<any>> =>
    api.get(`/workflows/${id}`, config),

  getStats: (userId?: number): Promise<AxiosResponse<any>> =>
    api.get('/workflows/stats', { params: userId != null ? { user_id: userId } : {} }),

  // skipErrorToast — workflowStore.createWorkflow renders its own error
  // toast (trial-aware message + Upgrade action on a plan-limit 403); the
  // global interceptor toasting too would double it.
  create: (data: any): Promise<AxiosResponse<any>> =>
    api.post('/workflows/', data, { skipErrorToast: true } as any),

  update: (id: number, data: any): Promise<AxiosResponse<any>> =>
    api.put(`/workflows/${id}`, data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/workflows/${id}`),

  // The workflow store shows its own toast for activation failures (validation
  // errors like a missing Create Task node); skip the interceptor's raw-detail
  // toast so the message isn't shown twice.
  // `approverId` is read only for a Representative (whose activate is a
  // request): the Owner/Admin/Manager it goes to. Omitted = account owner.
  activate: (id: number, approverId?: number | null): Promise<AxiosResponse<any>> =>
    api.post(
      `/workflows/${id}/activate`,
      approverId != null ? { approver_id: approverId } : undefined,
      { skipErrorToast: true } as any,
    ),

  deactivate: (id: number): Promise<AxiosResponse<any>> =>
    api.post(`/workflows/${id}/deactivate`, undefined, { skipErrorToast: true } as any),

  duplicate: (id: number): Promise<AxiosResponse<any>> =>
    api.post(`/workflows/${id}/duplicate`),

  /** Feeds the Assign Owner node's teammate picker -- every account member
   *  plus the account's plan name (smb/enterprise gets a closed dropdown;
   *  standard stays free-text/self-assign, see AssignOwnerConfig). */
  assignableMembers: (): Promise<AxiosResponse<AssignableMembersResponse>> =>
    api.get('/workflows/assignable-members'),

  pendingApprovals: (): Promise<AxiosResponse<PendingApproval[]>> =>
    api.get('/workflows/pending-approvals'),

  approveActivation: (id: number): Promise<AxiosResponse<any>> =>
    api.post(`/workflows/${id}/approve-activation`, undefined, { skipErrorToast: true } as any),

  // A reason is required (the Rep sees it on the rejected workflow).
  rejectActivation: (id: number, reason: string): Promise<AxiosResponse<any>> =>
    api.post(`/workflows/${id}/reject-activation`, { reason }, { skipErrorToast: true } as any),

  previewEmail: (config: {
    material_ids: number[]
    contact_fields: string[]
    tone: string
    purpose: string
    custom_instructions: string
    contact_id?: number
    email_step?: number
    total_emails?: number
    previous_emails?: { subject: string; body: string; step: number }[]
    reply_scenario?: string
    reply_content?: string
    workflow_id?: number
    node_id?: string
    template_id?: number
  }): Promise<AxiosResponse<{ subject: string; body: string; blocks?: { name: string; content: string }[]; contact_name?: string; discussion?: any[]; is_goodbye?: boolean; email_step?: number; total_emails?: number }>> =>
    api.post('/workflows/preview-email', config),

  renderTemplatePreview: (payload: {
    subject?: string
    body?: string
    contact_id: number
    sender_email_id?: number
  }): Promise<AxiosResponse<{ subject: string; body: string }>> =>
    api.post('/workflows/render-template-preview', payload),

  testNode: (payload: {
    node_type: string
    node_data: Record<string, any>
    contact_id?: number | null
    test_config?: {
      send_to?: 'self' | 'test_address' | 'real_contact'
      test_email?: string
      test_phone?: string
      sender_email_id?: number
      pregenerated_subject?: string
      pregenerated_body?: string
    }
    deck_attachment?: { download_url?: string; filename?: string; material_id?: number }
  }): Promise<AxiosResponse<{
    success: boolean
    node_type: string
    result: {
      message: string
      error?: string
      details?: Record<string, any>
    }
    duration_ms: number
  }>> =>
    api.post('/workflows/test-node', payload, { timeout: 300000 }),

  getTemplateVariables: (): Promise<AxiosResponse<Record<string, { field: string; label: string; token: string }[]>>> =>
    api.get('/workflows/template-variables'),

  aiTransform: (payload: {
    text: string
    action: string
    tone?: string
    field_type: 'subject' | 'body'
    available_variables?: string[]
  }): Promise<AxiosResponse<{ transformed_text: string; action: string; credits_used: number }>> =>
    api.post('/workflows/ai-transform', payload),

  aiGenerateChat: (data: { messages: { role: string; content: string }[]; target_plan?: any }, config?: { signal?: AbortSignal }): Promise<AxiosResponse<{
    message: string
    phase: 'questioning' | 'plan_ready'
    plan?: {
      workflow_name: string
      workflow_description: string
      nodes: { type: string; label: string; description: string; config: Record<string, any> }[]
      edges: { from_node_index: number; to_node_index: number; source_handle?: string; target_handle?: string }[]
    }
    credits_used: number
  }>> =>
    api.post('/workflows/ai-generate/chat', data, config),

  aiGenerateCreate: (data: {
    plan: any
    workflow_name?: string
    messages?: { role: string; content: string }[]
  }): Promise<AxiosResponse<{
    workflow_id: number
    workflow_name: string
    num_nodes: number
    num_emails: number
    status: string
  }>> =>
    api.post('/workflows/ai-generate/create', data),

  // Modify the open workflow via chat: send the live canvas + conversation,
  // get back the full updated canvas (full re-generate + re-layout).
  aiEditWorkflow: (data: {
    messages: { role: string; content: string }[]
    nodes: any[]
    edges: any[]
    workflow_name?: string
    workflow_description?: string
  }): Promise<AxiosResponse<{
    message: string
    nodes: any[]
    edges: any[]
    workflow_name: string
    workflow_description: string
    credits_used: number
  }>> =>
    api.post('/workflows/ai-generate/edit', data, { timeout: 300000 }),
}

export interface CustomWorkflowTemplate {
  id: number
  name: string
  description: string
  category: string
  nodes: any[]
  edges: any[]
  created_by: string
  created_by_id: number
}

// Custom workflow templates -- account-scoped templates a user saves from
// the builder (Owner/Admin/Manager only), shown alongside the static
// catalog in frontend/src/pages/templates/workflowTemplates.ts.
// See docs/superpowers/specs/2026-08-21-workflow-templates-design.md.
export const workflowTemplatesApi = {
  list: (): Promise<AxiosResponse<CustomWorkflowTemplate[]>> =>
    api.get('/workflow-templates/'),

  create: (data: {
    name: string
    description?: string
    category: string
    nodes: any[]
    edges: any[]
  }): Promise<AxiosResponse<CustomWorkflowTemplate>> =>
    api.post('/workflow-templates/', data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/workflow-templates/${id}`),
}

// Materials API endpoints
export const materialsApi = {
  getAll: (): Promise<AxiosResponse<any[]>> =>
    api.get('/materials/'),

  getById: (id: number): Promise<AxiosResponse<any>> =>
    api.get(`/materials/${id}`),

  getDetail: (id: number): Promise<AxiosResponse<any>> =>
    api.get(`/materials/${id}/detail`),

  upload: (file: File, title?: string, tags?: string[]): Promise<AxiosResponse<any>> => {
    const formData = new FormData()
    formData.append('file', file)
    if (title) formData.append('title', title)
    if (tags && tags.length > 0) formData.append('tags', JSON.stringify(tags))
    return api.post('/materials/upload', formData, {
      // 10 min: covers slow connections + multi-MB Office files
      // streaming through the backend → Azure Blob path.
      timeout: 600000,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // The caller renders its own batch summary toast; skip the interceptor's
      // per-request toast so a failure is not reported twice.
      skipErrorToast: true,
    } as any)
  },

  uploadUrl: (data: { url: string; title?: string; tags?: string[] }): Promise<AxiosResponse<any>> =>
    // The caller renders its own error toast; skip the interceptor's toast so a
    // failure shows only once (not interceptor + caller).
    api.post('/materials/upload-url', data, { skipErrorToast: true } as any),

  update: (id: number, data: { title?: string; description?: string; tags?: string[] }): Promise<AxiosResponse<any>> =>
    // Both callers (rename on the card, tag editor in the drawer) render their
    // own error toast; skip the interceptor's so a failure shows only once.
    api.patch(`/materials/${id}`, data, { skipErrorToast: true } as any),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/materials/${id}`),

  analyze: (id: number): Promise<AxiosResponse<any>> =>
    api.post(`/materials/${id}/analyze`),

  getSlides: (id: number, retry = false): Promise<AxiosResponse<any>> =>
    api.get(`/materials/${id}/slides`, retry ? { params: { retry: true } } : undefined),

  getPreviewBlob: async (id: number, opts?: { skipErrorToast?: boolean }): Promise<string> => {
    const res = await api.get(`/materials/${id}/preview`, {
      responseType: 'blob',
      // Download callers render their own error toast; let them opt out of the
      // interceptor's (which can't read a Blob error body anyway).
      ...(opts?.skipErrorToast ? { skipErrorToast: true } : {}),
    } as any)
    return URL.createObjectURL(res.data)
  },

  getInsights: (): Promise<AxiosResponse<any>> =>
    api.get('/materials/insights'),

  generateInsights: (): Promise<AxiosResponse<any>> =>
    api.post('/materials/insights/generate'),

  // Materials page "Generate with AI": creates the material row immediately
  // (status "processing") and runs the deck pipeline in the background —
  // the materials list poll picks up completion. Job is pollable via
  // presentationsApi.pollJob if inline progress is ever needed.
  generateDeck: (data: Record<string, unknown>): Promise<AxiosResponse<{ material_id: number; job_id: string; status: string }>> =>
    // The modal renders its own error toast (including the 402 insufficient-
    // credits case); skip the interceptor's so a failure shows only once.
    api.post('/materials/generate-deck', data, { skipErrorToast: true } as any),
}

// Team API endpoints
export const teamApi = {
  listMembers: (): Promise<AxiosResponse<TeamMember[]>> =>
    api.get('/team/members'),
  updateMemberRole: (userId: number, role: string): Promise<AxiosResponse<TeamMember>> =>
    api.patch(`/team/members/${userId}`, { role }),
  removeMember: (userId: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/team/members/${userId}`),
  /**
   * Hand the account to another member (AB#22363). Owner only. The caller is
   * demoted to Admin, and the account's plan + credit pool move with the
   * ownership, so the caller's own permissions change as a result — refetch
   * the permission map afterwards.
   */
  transferOwnership: (newOwnerId: number): Promise<AxiosResponse<TransferOwnershipResult>> =>
    api.post('/team/transfer-ownership', { new_owner_id: newOwnerId }),
  listInvites: (): Promise<AxiosResponse<Invite[]>> =>
    api.get('/team/invites'),
  createInvite: (data: { email: string; role: string }): Promise<AxiosResponse<Invite>> =>
    api.post('/team/invites', data),
  revokeInvite: (inviteId: number): Promise<AxiosResponse<{ message: string }>> =>
    api.delete(`/team/invites/${inviteId}`),
  validateInvite: (token: string): Promise<AxiosResponse<InviteValidation>> =>
    api.get(`/team/invites/${token}`),
  inviteEligibility: (): Promise<AxiosResponse<InviteEligibility>> =>
    api.get('/team/invite-eligibility'),
  /** Paid vs occupied seats, so "Add member" knows whether to invite, buy
   *  seats first, or route to Enterprise. Owner/Admin only, like inviting. */
  // skipErrorToast: the store treats any failure as "seat picture unknown"
  // and lets the server rule on the actual invite -- an API still on the
  // previous release answers 404 here, and that must not toast on the page.
  seatStatus: (): Promise<AxiosResponse<TeamSeatStatus>> =>
    api.get('/team/seats', { skipErrorToast: true } as any),
  acceptInvite: (
    token: string,
    data: { full_name?: string; password?: string }
  ): Promise<AxiosResponse<{ access_token: string; refresh_token: string; token_type: string }>> =>
    api.post(`/team/invites/${token}/accept`, data),
}

// Audit Log API endpoints
export const auditLogApi = {
  list: (params?: { limit?: number; skip?: number }): Promise<AxiosResponse<AuditLogEntry[]>> =>
    api.get('/audit-log/', { params }),
}

// Contacts API endpoints
export const contactsApi = {
  getAll: (params?: { search?: string; limit?: number; skip?: number }): Promise<AxiosResponse<any[]>> =>
    api.get('/contacts/', { params }),

  getById: (id: number): Promise<AxiosResponse<any>> =>
    api.get(`/contacts/${id}`),

  create: (data: any): Promise<AxiosResponse<any>> =>
    api.post('/contacts/', data),

  update: (id: number, data: any): Promise<AxiosResponse<any>> =>
    api.put(`/contacts/${id}`, data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/contacts/${id}`),

  research: (id: number): Promise<AxiosResponse<any>> =>
    api.post(`/contacts/${id}/research`),

  sendEmail: (
    id: number,
    data: {
      subject: string
      body: string
      sender_email_id?: number
      material_ids?: number[]
      include_signature?: boolean
    }
  ): Promise<AxiosResponse<{ status: string; message_id?: string; to: string; sender_email_id: number }>> =>
    api.post(`/contacts/${id}/send`, data),

  importCsv: (file: File): Promise<AxiosResponse<any>> => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/contacts/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  getTags: (): Promise<AxiosResponse<{ id?: number; name: string; count: number }[]>> =>
    api.get('/contacts/tags'),

  createTag: (name: string): Promise<AxiosResponse<{ id: number; name: string; count: number }>> =>
    api.post('/contacts/tags', { name }),

  deleteTag: (name: string): Promise<AxiosResponse<{ deleted: boolean; untagged: number }>> =>
    api.delete('/contacts/tags', { params: { name } }),
}

// Campaigns API endpoints
/** One entry of the deletion-approval queue (GET /contacts/deletion-requests). */
export interface PendingDeletion {
  id: number
  kind: 'contacts' | 'list'
  summary: string
  item_count: number
  list_id: number | null
  requested_by_user_id: number
  requested_by_name: string
  requested_by_email: string
  requested_at: string | null
}

export interface DeletionApprovalResult {
  success: boolean
  kind: 'contacts' | 'list'
  summary?: string | null
  deleted_count: number
  skipped_in_active_campaigns: number
}

/** What a gated delete returns instead of deleting (a Representative's request). */
export interface PendingApprovalResponse {
  status: 'pending_approval'
  request_id: number
  message: string
}

// A Representative's deletions (contacts, or a whole list) wait for a
// Manager/Admin/Owner; this is that approver queue. Mirrors the CRM export
// approvals in integrationsApi.
export const deletionRequestsApi = {
  list: (): Promise<AxiosResponse<PendingDeletion[]>> =>
    api.get('/contacts/deletion-requests'),

  approve: (id: number): Promise<AxiosResponse<DeletionApprovalResult>> =>
    api.post(`/contacts/deletion-requests/${id}/approve`),

  reject: (id: number, reason?: string): Promise<AxiosResponse<{ success: boolean }>> =>
    api.post(`/contacts/deletion-requests/${id}/reject`, { reason }),
}

export const campaignsApi = {
  // userId filters to one teammate's campaigns — honored server-side only for
  // account-wide scopes (Owner/Admin/Manager/Analyst); a Rep naming anyone
  // but themselves gets a 403.
  getAll: (skip = 0, limit = 50, userId?: number): Promise<AxiosResponse<CampaignListItem[]>> =>
    api.get('/campaigns/', { params: { skip, limit, ...(userId != null ? { user_id: userId } : {}) } }),

  getById: (id: number): Promise<AxiosResponse<Campaign>> =>
    api.get(`/campaigns/${id}`),

  getStats: (userId?: number): Promise<AxiosResponse<CampaignStats>> =>
    api.get('/campaigns/stats', { params: userId != null ? { user_id: userId } : {} }),

  // Teammates + campaign counts for the "ran by" filter dropdown. Own-scoped
  // roles get just themselves back.
  getMembers: (): Promise<AxiosResponse<CampaignMember[]>> =>
    api.get('/campaigns/members'),

  getStageProgress: (id: number): Promise<AxiosResponse<CampaignStageProgress>> =>
    api.get(`/campaigns/${id}/stage-progress`),

  getSentEmails: (id: number): Promise<AxiosResponse<SentEmailItem[]>> =>
    api.get(`/campaigns/${id}/sent-emails`),

  // skipErrorToast — campaignStore.createCampaign renders its own error
  // toast (trial-aware message + Upgrade action on a plan-limit 403); the
  // global interceptor toasting too would double it.
  create: (data: CreateCampaignData): Promise<AxiosResponse<Campaign>> =>
    api.post('/campaigns/', data, { skipErrorToast: true } as any),

  update: (id: number, data: UpdateCampaignData): Promise<AxiosResponse<Campaign>> =>
    api.put(`/campaigns/${id}`, data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/campaigns/${id}`),

  // For a Representative this queues an approval request and comes back with
  // status 'pending_approval' rather than 'running' — callers must read the
  // returned status instead of assuming the campaign started.
  launch: (id: number, approverId?: number | null): Promise<AxiosResponse<Campaign>> =>
    api.post(`/campaigns/${id}/launch`, approverId != null ? { approver_id: approverId } : undefined),

  // Everything an approver validates: audience, sender, signature, workflow,
  // details. Owner/Admin/Manager only.
  review: (id: number): Promise<AxiosResponse<CampaignReview>> =>
    api.get(`/campaigns/${id}/review`),

  // Launch-approval queue (Owner/Admin/Manager). skipErrorToast: the store
  // surfaces the server's detail itself, exactly like workflow approvals.
  pendingApprovals: (): Promise<AxiosResponse<PendingCampaignApproval[]>> =>
    api.get('/campaigns/pending-approvals'),

  approveLaunch: (id: number): Promise<AxiosResponse<Campaign>> =>
    api.post(`/campaigns/${id}/approve-launch`, undefined, { skipErrorToast: true } as any),

  rejectLaunch: (id: number, reason: string): Promise<AxiosResponse<Campaign>> =>
    api.post(`/campaigns/${id}/reject-launch`, { reason }, { skipErrorToast: true } as any),

  pause: (id: number): Promise<AxiosResponse<Campaign>> =>
    api.post(`/campaigns/${id}/pause`),

  resume: (id: number): Promise<AxiosResponse<Campaign>> =>
    api.post(`/campaigns/${id}/resume`),

  // AI Campaign Generator
  aiChat: (data: { messages: { role: string; content: string }[] }): Promise<AxiosResponse<{
    message: string
    phase: 'questioning' | 'plan_ready'
    plan?: {
      workflow_name: string
      workflow_description: string
      tone: string
      num_emails: number
      emails: { step: number; purpose: string; subject: string; body: string }[]
      delays: { after_step: number; delay_value: number; delay_unit: string }[]
      include_exit_condition: boolean
    }
    credits_used: number
  }>> =>
    api.post('/campaigns/ai-generate/chat', data),

  aiCreate: (data: {
    plan: any
    sender_email_id?: number
    contact_list_id?: number
    target_all_contacts?: boolean
    scheduled_start: string
    campaign_name?: string
  }): Promise<AxiosResponse<{ workflow_id: number; campaign_id: number; campaign_name: string; total_recipients: number }>> =>
    api.post('/campaigns/ai-generate/create', data),

  // DISABLED — "Suggested campaigns" is switched off. The backend route is
  // unregistered (see routes/ai_campaign.py), so calling this would 404 and
  // still cost a round trip; commented out so nothing can invoke it by mistake.
  // Re-enable together with the backend decorator and the Campaigns page block.
  // aiSuggestions: (): Promise<AxiosResponse<{
  //   suggestions: { title: string; description: string; icon_type: string; prompt: string }[]
  // }>> =>
  //   api.get('/campaigns/ai-generate/suggestions'),
}

// Sender Emails API endpoints
export interface NotificationItem {
  id: number
  type: string
  title: string
  message?: string | null
  link?: string | null
  is_read: boolean
  created_at?: string | null
}

export const notificationsApi = {
  getAll: (limit = 20): Promise<AxiosResponse<{ items: NotificationItem[]; unread_count: number }>> =>
    api.get('/notifications/', { params: { limit } }),

  unreadCount: (): Promise<AxiosResponse<{ count: number }>> =>
    api.get('/notifications/unread-count'),

  markRead: (id: number): Promise<AxiosResponse<{ success: boolean }>> =>
    api.post(`/notifications/${id}/read`),

  markAllRead: (): Promise<AxiosResponse<{ success: boolean; updated: number }>> =>
    api.post('/notifications/read-all'),

  remove: (id: number): Promise<AxiosResponse<{ success: boolean }>> =>
    api.delete(`/notifications/${id}`),

  // Manual test helper — creates a sample notification for the current user.
  createTest: (): Promise<AxiosResponse<NotificationItem>> =>
    api.post('/notifications/test'),
}

// Approval records (who a Representative may send a request to, and the
// Rep's own submissions). See types/approvals.ts.
export const approvalsApi = {
  approvers: (): Promise<AxiosResponse<ApproversResponse>> =>
    api.get('/approvals/approvers'),

  myRequests: (): Promise<AxiosResponse<MyApprovalRequest[]>> =>
    api.get('/approvals/my-requests'),
}

// Per-user email signature (Momentum/Enterprise/Trial). GET returns null
// when the current user hasn't saved one yet; a plan gate 403s with a
// "plan" message, which signatureStore turns into a locked state rather
// than a toast.
export const signaturesApi = {
  getMine: (): Promise<AxiosResponse<EmailSignature | null>> =>
    api.get('/signatures/me', { skipErrorToast: true } as any),

  saveMine: (html: string): Promise<AxiosResponse<EmailSignature>> =>
    api.put('/signatures/me', { html }, { skipErrorToast: true } as any),

  deleteMine: (): Promise<AxiosResponse<void>> =>
    api.delete('/signatures/me', { skipErrorToast: true } as any),
}

export const senderEmailsApi = {
  getAll: (): Promise<AxiosResponse<SenderEmail[]>> =>
    api.get('/sender-emails/'),

  getVerified: (): Promise<AxiosResponse<SenderEmail[]>> =>
    api.get('/sender-emails/verified'),

  /**
   * Mailbox addresses only, for the Analytics SMTP filter. Behind
   * `sender_emails.list_addresses`, which every role holds -- unlike
   * `getAll`, whose `sender_emails.manage` cell excludes Analyst (AB#22341).
   */
  getAddresses: (): Promise<AxiosResponse<SenderEmailAddress[]>> =>
    api.get('/sender-emails/addresses'),

  getById: (id: number): Promise<AxiosResponse<SenderEmail>> =>
    api.get(`/sender-emails/${id}`),

  /**
   * Which provider an SMTP hostname belongs to, and the daily limit that
   * follows. A PREVIEW for the add-sender review step: the create route detects
   * independently from its own payload, so nothing here decides the stored cap
   * (core/smtp_providers.py).
   */
  detectProvider: (
    smtpServer: string,
    emailAddress: string,
  ): Promise<AxiosResponse<DetectedProvider>> =>
    api.get('/sender-emails/detect-provider', {
      params: { smtp_server: smtpServer, email_address: emailAddress },
    }),

  create: (data: CreateSenderEmailData): Promise<AxiosResponse<SenderEmail>> =>
    api.post('/sender-emails/', data),

  update: (id: number, data: Partial<CreateSenderEmailData>): Promise<AxiosResponse<SenderEmail>> =>
    api.put(`/sender-emails/${id}`, data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/sender-emails/${id}`),

  verify: (id: number): Promise<AxiosResponse<any>> =>
    api.post(`/sender-emails/${id}/verify`),

  checkDns: (id: number): Promise<AxiosResponse<any>> =>
    api.post(`/sender-emails/${id}/check-dns`),

  setDefault: (id: number): Promise<AxiosResponse<SenderEmail>> =>
    api.post(`/sender-emails/${id}/set-default`),

  // Replace the set of teammates granted this mailbox (empty array =
  // unassigned, usable by owners/admins only). Owner/Admin only —
  // PATCH /sender-emails/{id}/assign.
  assign: (id: number, assignedUserIds: number[]): Promise<AxiosResponse<SenderEmail>> =>
    api.patch(`/sender-emails/${id}/assign`, { assigned_user_ids: assignedUserIds }),

  // Nylas OAuth mailbox connection (F8). Returns the hosted-auth URL to
  // redirect the browser to; the backend callback does the rest.
  oauthAuthorize: (
    provider: 'google' | 'microsoft'
  ): Promise<AxiosResponse<{ authorize_url: string }>> =>
    api.get(`/sender-emails/oauth/${provider}/authorize`),

  // Which "Connect with <provider>" buttons the backend currently allows —
  // each provider has its own independent NYLAS_*_CONNECT_ENABLED switch.
  getOAuthProviders: (): Promise<AxiosResponse<{ google: boolean; microsoft: boolean }>> =>
    api.get('/sender-emails/oauth/providers'),
}

// Inbox API endpoints
export const inboxApi = {
  getAll: (params?: { skip?: number; limit?: number; sentiment?: string; intent?: string; campaign_id?: number; sender_email_id?: number; search?: string; from_address?: string; contact_user_id?: number; unassigned?: boolean; all_members?: boolean }): Promise<AxiosResponse<InboxMessage[]>> =>
    api.get('/inbox/', { params }),

  getSenders: (params?: { skip?: number; limit?: number; search?: string; sender_email_id?: number }): Promise<AxiosResponse<InboxSendersResponse>> =>
    api.get('/inbox/senders', { params }),

  getContacts: (params?: { skip?: number; limit?: number; search?: string }): Promise<AxiosResponse<InboxContactsResponse>> =>
    api.get('/inbox/contacts', { params }),

  // Accounts sharing the given mailbox (by its address), for the per-user filter
  // dropdown. sender_email_id must be one of the caller's own sender rows.
  // The mailboxes the Inbox can be opened on (every role with inbox.view).
  getMailboxes: (): Promise<AxiosResponse<InboxMailbox[]>> =>
    api.get('/inbox/mailboxes'),
  // No sender_email_id asks for the union across every mailbox the caller
  // shares, which is what the "All mailboxes" view needs.
  getMailboxUsers: (params: { sender_email_id?: number }): Promise<AxiosResponse<InboxMailboxUsersResponse>> =>
    api.get('/inbox/mailbox-users', { params }),

  getStats: (params?: { sender_email_id?: number; contact_user_id?: number; unassigned?: boolean; all_members?: boolean }): Promise<AxiosResponse<InboxStats>> =>
    api.get('/inbox/stats', { params }),

  getById: (id: number): Promise<AxiosResponse<InboxMessage>> =>
    api.get(`/inbox/${id}`),

  getByCampaign: (campaignId: number): Promise<AxiosResponse<InboxMessage[]>> =>
    api.get(`/inbox/campaign/${campaignId}`),

  getPendingApprovals: (): Promise<AxiosResponse<InboxMessage[]>> =>
    api.get('/inbox/pending-approvals'),

  markAsRead: (id: number): Promise<AxiosResponse<{ id: number; is_read: boolean }>> =>
    api.post(`/inbox/${id}/read`),

  analyze: (id: number): Promise<AxiosResponse<any>> =>
    api.post(`/inbox/${id}/analyze`),

  analyzeUnanalyzed: (): Promise<AxiosResponse<{ queued: number; failed: number; total_unanalyzed: number }>> =>
    api.post('/inbox/analyze-unanalyzed'),

  reply: (id: number, data: { content: string }): Promise<AxiosResponse<any>> =>
    api.post(`/inbox/${id}/reply`, data),

  approveReply: (id: number): Promise<AxiosResponse<any>> =>
    api.post(`/inbox/${id}/approve-reply`),

  rejectReply: (id: number): Promise<AxiosResponse<any>> =>
    api.post(`/inbox/${id}/reject-reply`),
}

// Auto Reply Rules API endpoints
export const autoReplyRulesApi = {
  getAll: (): Promise<AxiosResponse<AutoReplyRule[]>> =>
    api.get('/auto-reply-rules/'),

  create: (data: CreateAutoReplyRuleData): Promise<AxiosResponse<AutoReplyRule>> =>
    api.post('/auto-reply-rules/', data),

  update: (id: number, data: Partial<CreateAutoReplyRuleData & { is_active?: boolean }>): Promise<AxiosResponse<AutoReplyRule>> =>
    api.put(`/auto-reply-rules/${id}`, data),

  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/auto-reply-rules/${id}`),
}

// Apollo / Integrations API endpoints
export interface IntegrationStatus {
  apollo: boolean; hunter: boolean; snov: boolean; rocketreach: boolean;
  peopledatalabs: boolean; lusha: boolean; uplead: boolean;
  hubspot: boolean; pipedrive: boolean; salesforce: boolean; zoho: boolean;
  slack: boolean; calendly: boolean; twilio: boolean; cognism: boolean;
  teams: boolean; zoom: boolean; google_calendar: boolean; outlook_calendar: boolean;
}

export interface CrmExportResult {
  success: boolean;
  provider: string;
  total: number;
  exported: number;
  failed: number;
  skipped: number;
  errors: Array<{ email?: string; error?: string }>;
  /** Present only when a Representative's export was queued instead of run:
   *  nothing has reached the CRM yet, an approver has to release it. */
  status?: 'pending_approval';
  request_id?: number;
}

/** Which CRMs the export picker may offer — see GET /integrations/crm/availability.
 *  Unlike IntegrationStatus this is readable by anyone who may export or request
 *  an export, and for a Representative it answers for the ACCOUNT's connections. */
export interface CrmAvailability {
  hubspot: boolean;
  pipedrive: boolean;
  salesforce: boolean;
  zoho: boolean;
}

/** A Representative's queued CRM export, awaiting an approver. */
export interface PendingCrmExport {
  id: number;
  provider: string;
  /** Human label for what was picked: "Selected contacts", "Contact list", "Tag: x". */
  source: string;
  list_id: number | null;
  tag: string | null;
  contact_count: number;
  requested_by_user_id: number;
  requested_by_name: string;
  requested_by_email: string;
  requested_at: string | null;
}

/** A CRM export request someone already approved or rejected. */
export interface DecidedCrmExport extends PendingCrmExport {
  status: 'approved' | 'rejected';
  reason: string | null;
  decided_by_name: string | null;
  decided_at: string | null;
}

export const integrationsApi = {
  getStatus: (): Promise<AxiosResponse<IntegrationStatus>> =>
    api.get('/integrations/status'),

  // Bulk-push contacts (a selection, or a whole list) to a connected CRM.
  // For a Representative this QUEUES the export instead (status: pending_approval).
  crmExportContacts: (data: { provider: string; contact_ids?: number[]; list_id?: number; tag?: string }): Promise<AxiosResponse<CrmExportResult>> =>
    api.post('/integrations/crm/export-contacts', data),

  // Which CRMs the export picker may offer. Readable by every role that may
  // export or request one, unlike getStatus() which is Owner/Admin/Manager-only.
  crmAvailability: (): Promise<AxiosResponse<CrmAvailability>> =>
    api.get('/integrations/crm/availability'),

  // Approver queue for Representatives' export requests.
  crmExportRequests: (): Promise<AxiosResponse<PendingCrmExport[]>> =>
    api.get('/integrations/crm/export-requests'),

  crmExportDecided: (): Promise<AxiosResponse<DecidedCrmExport[]>> =>
    api.get('/integrations/crm/export-requests/decided'),

  approveCrmExport: (id: number): Promise<AxiosResponse<CrmExportResult>> =>
    api.post(`/integrations/crm/export-requests/${id}/approve`),

  rejectCrmExport: (id: number, reason?: string): Promise<AxiosResponse<{ success: boolean }>> =>
    api.post(`/integrations/crm/export-requests/${id}/reject`, { reason }),

  // Browse a CRM's contacts (paged via an opaque cursor) for import.
  crmContacts: (data: { provider: string; cursor?: string | null; per_page?: number }): Promise<AxiosResponse<{ success: boolean; people: any[]; next_cursor: string | null }>> =>
    api.post('/integrations/crm/contacts', data),

  // Pull selected CRM records into our contacts DB.
  crmImport: (data: { provider: string; people: any[]; tags?: string[]; list_id?: number }): Promise<AxiosResponse<{ success: boolean; imported: number; duplicates: number; errors: number; error_details: any[]; list_id: number | null; added_to_list: number | null }>> =>
    api.post('/integrations/crm/import', data),

  testConnection: (service: string, apiKey: string): Promise<AxiosResponse<{ success: boolean; message: string }>> =>
    api.post('/integrations/test', null, { params: { service, api_key: apiKey } }),

  // Fire-and-forget on login: re-tests every configured integration in the
  // background so the Settings status dots are fresh without manual testing.
  // A Rep/Analyst has no PERMISSIONS entry for "integrations.manage" at all,
  // so this call is called unconditionally at login for every role and
  // always 403s "Not permitted" for those two -- authStore's own caller
  // already does .catch(() => {}), but that only stops the rejection from
  // going unhandled; it doesn't stop the interceptor's OWN toast, which
  // fires synchronously before the promise ever gets back to the caller.
  // skipErrorToast is what actually silences it, same as every other
  // fire-and-forget/self-toasting call in this file (warmupApi, materials
  // upload, the connection-test endpoints below).
  refreshStatuses: (): Promise<AxiosResponse<{ status: string }>> =>
    api.post('/integrations/refresh-statuses', undefined, { skipErrorToast: true } as any),

  apolloSearch: (data: {
    query?: string
    titles?: string[]
    include_similar_titles?: boolean
    company_names?: string[]
    organization_ids?: string[]
    organization_domains?: string[]
    industries?: string[]
    locations?: string[]
    organization_locations?: string[]
    seniority_levels?: string[]
    employee_ranges?: string[]
    revenue_min?: number
    revenue_max?: number
    technologies?: string[]
    email_status?: string[]
    page?: number
    per_page?: number
    fetch_all?: boolean
    // Pairs with apolloSearchCancel — lets the backend abort this exact search.
    request_id?: string
  }, signal?: AbortSignal): Promise<AxiosResponse<{ success: boolean; people: any[]; total: number; page: number; per_page: number; partial?: boolean; warning?: string }>> =>
    // fetch_all sweeps every Apollo page server-side — give it room to run.
    api.post('/integrations/apollo/search', data, { timeout: 600000, signal }),

  // Cancels the server-side search task, aborting the outbound request to
  // Apollo's API — not just the browser call.
  apolloSearchCancel: (requestId: string): Promise<AxiosResponse<{ success: boolean; canceled: boolean }>> =>
    api.post(`/integrations/apollo/search/cancel/${encodeURIComponent(requestId)}`),

  apolloImport: (data: {
    people: any[]
    tag?: string
    tags?: string[]
    list_id?: number
  }): Promise<AxiosResponse<{ success: boolean; imported: number; duplicates: number; errors: number; error_details: any[]; list_id: number | null }>> =>
    api.post('/integrations/apollo/import', data),

  apolloBulkEnrich: (personIds: string[]): Promise<AxiosResponse<{
    success: boolean; enriched: any[]; credits_consumed: number
  }>> =>
    api.post('/integrations/apollo/enrich', { person_ids: personIds }),

  apolloEnrichContacts: (contactIds: number[]): Promise<AxiosResponse<{
    success: boolean; enriched: number; failed: number; total: number; results: any[]
  }>> =>
    // Caller (ContactDetailModal) renders its own friendly toast; skip the
    // interceptor's raw-detail toast to avoid a duplicate.
    api.post('/integrations/apollo/enrich-contacts', { contact_ids: contactIds }, { skipErrorToast: true } as any),

  apolloSearchOrganizations: (data: { query: string; page?: number; per_page?: number }): Promise<AxiosResponse<{
    success: boolean; organizations: Array<{ id: string; name: string; website_url?: string; industry?: string; estimated_num_employees?: number; logo_url?: string; city?: string; state?: string; country?: string }>; total: number
  }>> =>
    api.post('/integrations/apollo/organizations/search', data),

  apolloGetLists: (): Promise<AxiosResponse<{ success: boolean; lists: Array<{ id: string; name: string; created_at?: string; cached_count?: number }> }>> =>
    api.get('/integrations/apollo/lists'),

  apolloGetListContacts: (labelId: string, page?: number, perPage?: number, fetchAll?: boolean): Promise<AxiosResponse<{
    success: boolean; contacts: any[]; total: number; page: number; pages_fetched?: number; partial?: boolean
  }>> =>
    api.get(`/integrations/apollo/lists/${labelId}/contacts`, {
      params: { page: page || 1, per_page: perPage || 25, fetch_all: fetchAll || false },
    }),

  // Hunter.io endpoints
  hunterDomainSearch: (data: {
    domain: string
    limit?: number
    offset?: number
    type?: string
    seniority?: string[]
    department?: string[]
  }): Promise<AxiosResponse<{
    success: boolean; emails: any[]; pattern?: string; organization?: string; total: number; limit: number; offset: number
  }>> =>
    api.post('/integrations/hunter/domain-search', data),

  hunterFindEmail: (data: {
    domain: string
    first_name: string
    last_name: string
  }): Promise<AxiosResponse<{
    success: boolean; email?: string; confidence?: number; sources?: any[]
  }>> =>
    api.post('/integrations/hunter/find-email', data),

  hunterVerifyEmail: (data: {
    email: string
  }): Promise<AxiosResponse<{
    success: boolean; status?: string; result?: string; score?: number; email?: string;
    mx_records?: boolean; smtp_check?: boolean; disposable?: boolean; webmail?: boolean
  }>> =>
    api.post('/integrations/hunter/verify-email', data),

  hunterBulkVerify: (emails: string[]): Promise<AxiosResponse<{
    success: boolean; results: any[]; verified: number; total: number
  }>> =>
    api.post('/integrations/hunter/bulk-verify', { emails }),

  hunterPersonFind: (data: {
    email?: string; linkedin_handle?: string
  }): Promise<AxiosResponse<{ success: boolean; data?: any }>> =>
    api.post('/integrations/hunter/person-find', data),

  hunterCompanyFind: (data: {
    domain: string
  }): Promise<AxiosResponse<{ success: boolean; data?: any }>> =>
    api.post('/integrations/hunter/company-find', data),

  hunterImport: (data: {
    people: any[]; tag?: string
  }): Promise<AxiosResponse<{
    success: boolean; imported: number; duplicates: number; errors: number; error_details: any[]
  }>> =>
    api.post('/integrations/hunter/import', data),

  hunterEnrichContacts: (contactIds: number[]): Promise<AxiosResponse<{
    success: boolean; enriched: number; failed: number; total: number; results: any[]
  }>> =>
    // Caller renders its own friendly toast; skip the interceptor's raw-detail
    // toast to avoid a duplicate.
    api.post('/integrations/hunter/enrich-contacts', { contact_ids: contactIds }, { skipErrorToast: true } as any),

  // Snov.io endpoints
  snovFindEmail: (data: { first_name: string; last_name: string; domain: string }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/snov/find-email', data),
  snovDomainSearch: (data: { domain: string; limit?: number }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/snov/domain-search', data),

  // RocketReach endpoints
  rocketreachLookup: (data: { name?: string; current_employer?: string; linkedin_url?: string }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/rocketreach/lookup', data),
  rocketreachSearch: (data: { query?: string; title?: string; company_name?: string; location?: string; page?: number; page_size?: number }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/rocketreach/search', data),

  // People Data Labs endpoints
  pdlEnrichPerson: (data: { email?: string; linkedin_url?: string; first_name?: string; last_name?: string; company?: string }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/peopledatalabs/enrich-person', data),
  pdlEnrichCompany: (data: { domain?: string; name?: string }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/peopledatalabs/enrich-company', data),

  // Enrichment providers
  getEnrichmentProviders: (): Promise<AxiosResponse<{ providers: Array<{
    name: string
    label: string
    available: boolean
    config_schema: Record<string, any>
  }> }>> =>
    api.get('/enrichment/providers'),

  // HubSpot endpoints
  hubspotSyncContact: (data: { contact_id?: number; contact_data?: any }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/hubspot/sync-contact', data),
  hubspotSearch: (data: { query: string; limit?: number }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/hubspot/search', data),

  // Pipedrive endpoints
  pipedriveSyncContact: (data: { contact_id?: number; contact_data?: any }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/pipedrive/sync-contact', data),

  // Slack endpoints
  slackSendNotification: (data: { text: string }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/slack/send', data),

  // Calendly endpoints
  calendlyEventTypes: (): Promise<AxiosResponse<any>> =>
    api.get('/integrations/calendly/event-types'),
  calendlyEvents: (count?: number): Promise<AxiosResponse<any>> =>
    api.get('/integrations/calendly/events', { params: { count: count || 10 } }),

  // Twilio endpoints
  twilioSendSMS: (data: { to: string; body: string }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/twilio/send-sms', data),

  // Cognism
  cognismEnrichPerson: (data: { email?: string; linkedin_url?: string; first_name?: string; last_name?: string; company?: string }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/cognism/enrich-person', data),
  cognismEnrichCompany: (data: { domain?: string; name?: string }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/cognism/enrich-company', data),

  // Microsoft Teams
  teamsSendMessage: (data: { text: string; title?: string }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/teams/send', data),

  // Zoom
  zoomCreateMeeting: (data: { topic: string; start_time?: string; duration?: number; agenda?: string }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/zoom/create-meeting', data),
  zoomListMeetings: (): Promise<AxiosResponse<any>> =>
    api.get('/integrations/zoom/meetings'),

  // Salesforce
  salesforceSyncContact: (data: { contact_id?: number; contact_data?: any }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/salesforce/sync-contact', data),
  salesforceSearch: (data: { query: string; limit?: number }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/salesforce/search', data),

  // Zoho CRM
  zohoContacts: (data: { module?: 'Contacts' | 'Leads'; page?: number; per_page?: number }):
    Promise<AxiosResponse<{ success: boolean; people: any[]; more_records: boolean }>> =>
    api.post('/integrations/zoho/contacts', data),
  zohoImport: (data: { people: any[]; tags?: string[]; list_id?: number }):
    Promise<AxiosResponse<{ success: boolean; imported: number; duplicates: number; errors: number; error_details: any[]; list_id: number | null }>> =>
    api.post('/integrations/zoho/import', data),
  zohoSyncContact: (data: { contact_id?: number; contact_data?: any }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/zoho/sync-contact', data),
  zohoSearch: (data: { query: string; limit?: number; module?: 'Contacts' | 'Leads' }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/zoho/search', data),

  // Google Calendar
  googleCalendarListEvents: (calendarId?: string, maxResults?: number): Promise<AxiosResponse<any>> =>
    api.get('/integrations/google-calendar/events', { params: { calendar_id: calendarId || 'primary', max_results: maxResults || 10 } }),
  googleCalendarCreateEvent: (data: { calendar_id?: string; summary: string; start_time: string; end_time: string; description?: string; attendees?: string[] }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/google-calendar/events', data),

  // Outlook Calendar
  outlookCalendarListEvents: (userId?: string, top?: number): Promise<AxiosResponse<any>> =>
    api.get('/integrations/outlook-calendar/events', { params: { user_id: userId || 'me', top: top || 10 } }),
  outlookCalendarCreateEvent: (data: { user_id?: string; subject: string; start_time: string; end_time: string; body?: string; attendees?: string[] }): Promise<AxiosResponse<any>> =>
    api.post('/integrations/outlook-calendar/events', data),

  // LinkedIn OAuth - DISABLED / SHELVED (2026-08-28)
  //
  // The LinkedIn provider was removed from the Integrations page and the four
  // backing endpoints in backend/app/routes/integrations.py are commented out,
  // so these would 404. Kept here, commented, for a future re-enable - see the
  // restore checklist in backend/app/services/linkedin_service.py.
  //
  // linkedinAuthorize: (redirectUri: string): Promise<AxiosResponse<{ authorize_url: string; state: string }>> =>
  //   api.get('/integrations/linkedin/authorize', { params: { redirect_uri: redirectUri } }),
  //
  // linkedinCallback: (code: string, redirectUri: string): Promise<AxiosResponse<{ connected: boolean; profile_name?: string; profile_email?: string; expires_at?: string }>> =>
  //   api.post('/integrations/linkedin/callback', null, { params: { code, redirect_uri: redirectUri } }),
  //
  // linkedinDisconnect: (): Promise<AxiosResponse<{ connected: boolean }>> =>
  //   api.delete('/integrations/linkedin/disconnect'),
  //
  // linkedinStatus: (): Promise<AxiosResponse<{ connected: boolean; has_client_credentials: boolean; expires_at?: string }>> =>
  //   api.get('/integrations/linkedin/status'),

}

// Analytics API endpoints
export const onboardingApi = {
  getStatus: (): Promise<AxiosResponse<any>> =>
    api.get('/onboarding/status'),
}

export const analyticsApi = {
  getDashboard: (): Promise<AxiosResponse<any>> =>
    api.get('/analytics/dashboard'),

  getCampaignStats: (campaignId: number): Promise<AxiosResponse<any>> =>
    api.get(`/analytics/campaigns/${campaignId}`),

  getWorkflowStats: (workflowId: number): Promise<AxiosResponse<any>> =>
    api.get(`/analytics/workflows/${workflowId}`),

  getEmailPerformance: (): Promise<AxiosResponse<any>> =>
    api.get('/analytics/email-performance'),

  // user_id scopes the metrics to one teammate; omitted = whole shared-mailbox team.
  // The addresses the mailbox filter may offer: every address the team's
  // campaigns went out from, including mailboxes since deleted, plus the
  // caller's own live ones. NOT /sender-emails/addresses, which lists only
  // live rows and so offered no option matching a deleted mailbox's campaigns.
  getScopeMailboxes: (): Promise<AxiosResponse<{ items: { email_address: string; label: string | null }[] }>> =>
    api.get('/analytics/mailboxes'),
  getTeamMembers: (params?: { sender_email?: string }): Promise<AxiosResponse<{ items: { user_id: number; name: string | null; email: string }[] }>> =>
    api.get('/analytics/team-members', { params }),
  getOverview: (params?: { period?: string; start?: string; end?: string; user_id?: number; top_metric?: string }) =>
    api.get('/analytics/overview', { params }),
  getCampaigns: (params?: { period?: string; start?: string; end?: string; status?: string; sort_by?: string; sort_dir?: string; page?: number; page_size?: number; user_id?: number; sender_email?: string }) =>
    api.get('/analytics/campaigns', { params }),
  getCampaignDetail: (id: number, params?: { period?: string; start?: string; end?: string; user_id?: number; sender_email?: string }) =>
    api.get(`/analytics/campaigns/${id}`, { params }),
  getContacts: (params?: { period?: string; start?: string; end?: string; user_id?: number }) =>
    api.get('/analytics/contacts', { params }),
  getDeliverability: (params?: { period?: string; start?: string; end?: string; user_id?: number; sender_email?: string }) =>
    api.get('/analytics/deliverability', { params }),
  getReplies: (params?: { period?: string; start?: string; end?: string; user_id?: number; sender_email?: string }) =>
    api.get('/analytics/replies', { params }),

  // Overview KPIs as an .xlsx workbook. Row scope follows analytics.view
  // (Manager/Rep get their own numbers), so the same filters the page is
  // showing are passed straight through.
  exportReport: (
    params?: { period?: string; start?: string; end?: string; user_id?: number },
  ): Promise<AxiosResponse<Blob>> =>
    api.get('/analytics/export', {
      params,
      responseType: 'blob',
      // The helper toasts its own message; keep the interceptor from adding a
      // second one (and it cannot read a Blob error body anyway).
      skipErrorToast: true,
    } as any),
}

// Settings API endpoints (mapped to actual backend routes)
export const settingsApi = {
  getConfig: (): Promise<AxiosResponse<any>> =>
    api.get('/auth/config'),

  updateConfig: (data: any): Promise<AxiosResponse<any>> =>
    api.put('/auth/config', data),

  revealConfigKey: (field: string): Promise<AxiosResponse<{ field: string; value: string }>> =>
    api.get('/auth/config/reveal', { params: { field } }),

  updateProfile: (data: { full_name?: string; email?: string; username?: string }): Promise<AxiosResponse<any>> =>
    api.put('/auth/me', data),

  getLLMProviders: (): Promise<AxiosResponse<any>> =>
    api.get('/auth/llm-providers'),

  // These four all render their own toast/inline verdict with a specific
  // message (see settingsStore.ts). Without skipErrorToast, the global axios
  // interceptor ALSO fires a toast for the same error — every failed
  // connection test showed two stacked notifications, one of them a generic
  // "X connection failed" with no reason.
  testSmtpConnection: (): Promise<AxiosResponse<any>> =>
    api.post('/auth/test-email-connection', undefined, { skipErrorToast: true } as any),

  testLLMConnection: (provider: string, model: string): Promise<AxiosResponse<any>> =>
    api.post('/auth/test-llm', null, { params: { provider, model }, skipErrorToast: true } as any),

  // Verifies a key the user is still typing, so it goes in the body — a key in a
  // query string would land in access logs.
  testLLMKey: (provider: string, apiKey: string): Promise<AxiosResponse<LLMKeyTestResult>> =>
    api.post('/auth/test-llm-key', { provider, api_key: apiKey }, { skipErrorToast: true } as any),

  testIntegration: (service: string, apiKey: string): Promise<AxiosResponse<any>> =>
    api.post('/integrations/test', null, { params: { service, api_key: apiKey }, skipErrorToast: true } as any),

  getIntegrationStatus: (): Promise<AxiosResponse<any>> =>
    api.get('/integrations/status'),

  changePassword: (data: { current_password: string; new_password: string }): Promise<AxiosResponse<any>> =>
    api.put('/auth/change-password', data),
}

// Presentations API endpoints
export const presentationsApi = {
  /** Kick off a preview job — returns {job_id, status: "running"} immediately.
   *  Prefer `previewAndPoll` for end-to-end UX with progress events. */
  preview: (data: {
    material_ids: number[]
    contact_fields: string[]
    slide_count: number
    content_focus: string
    tone: string
    custom_instructions: string
    contact_id?: number
    color_theme?: string
    custom_colors?: { primary: string; accent: string; secondary: string }
    logo_url?: string
  }): Promise<AxiosResponse<{ job_id: string; status: string }>> =>
    api.post('/presentations/preview', data),

  /** Start a preview job and poll until complete, surfacing progress events. */
  previewAndPoll: async (
    data: {
      material_ids: number[]
      contact_fields: string[]
      slide_count: number
      content_focus: string
      tone: string
      custom_instructions: string
      contact_id?: number
      color_theme?: string
      custom_colors?: { primary: string; accent: string; secondary: string }
      logo_url?: string
    },
    onProgress?: (msg: string) => void,
  ): Promise<any> => {
    const startRes = await api.post('/presentations/preview', data)
    const jobId = startRes.data.job_id
    while (true) {
      await new Promise(r => setTimeout(r, 3000))
      const pollRes = await api.get(`/presentations/job/${jobId}`)
      const job = pollRes.data
      if (job.progress && onProgress) onProgress(job.progress)
      if (job.status === 'completed') return { data: job.result }
      if (job.status === 'failed') throw new Error(job.error || 'Preview generation failed')
    }
  },

  generate: (data: {
    material_ids: number[]
    contact_fields: string[]
    slide_count: number
    content_focus: string
    tone: string
    custom_instructions: string
    contact_id?: number
    color_theme?: string
    custom_colors?: { primary: string; accent: string; secondary: string }
    logo_url?: string
    output_format?: string
  }): Promise<AxiosResponse<{ job_id: string; status: string; download_url?: string; [key: string]: any }>> =>
    api.post('/presentations/generate', data, { timeout: 300000 }),

  pollJob: (jobId: string): Promise<AxiosResponse<{
    job_id: string
    status: string
    progress?: string
    result?: any
    error?: string
  }>> =>
    api.get(`/presentations/job/${jobId}`),

  /** Start generation and poll until complete. Calls onProgress with status updates. */
  generateAndPoll: async (
    data: {
      material_ids: number[]
      contact_fields: string[]
      slide_count: number
      content_focus: string
      tone: string
      custom_instructions: string
      contact_id?: number
      color_theme?: string
      custom_colors?: { primary: string; accent: string; secondary: string }
      logo_url?: string
      output_format?: string
    },
    onProgress?: (msg: string) => void,
  ): Promise<any> => {
    const startRes = await api.post('/presentations/generate', data)
    const jobId = startRes.data.job_id

    // Poll every 3 seconds
    while (true) {
      await new Promise(r => setTimeout(r, 3000))
      const pollRes = await api.get(`/presentations/job/${jobId}`)
      const job = pollRes.data

      if (job.progress && onProgress) {
        onProgress(job.progress)
      }

      if (job.status === 'completed') {
        return { data: job.result }
      }
      if (job.status === 'failed') {
        throw new Error(job.error || 'Generation failed')
      }
    }
  },

  /** Render existing slide JSON to PDF without re-running the LLM pipeline. */
  /** Render pre-generated slide JSON to a PDF or PPTX file. No LLM calls —
   *  this just runs the file builder against slides that came back from
   *  /preview. Use this for Download PDF / Download PPTX buttons that
   *  already have a previewed slide deck in state. */
  renderSlides: (data: {
    title: string
    slides: any[]
    color_theme?: Record<string, string>
    // Live theme-selector key (e.g. 'dark_mode', 'custom', 'auto_from_brand').
    // Re-resolved fresh server-side so picking a different swatch after Run
    // Test doesn't get silently discarded — see RenderSlidesRequest in
    // presentations.py.
    color_theme_key?: string
    custom_colors?: Record<string, string>
    output_format?: 'pdf' | 'pptx'
    // Ask the server to also rasterize the built PPTX to WebP preview images
    // (no extra LLM cost — just a LibreOffice conversion of the file that's
    // about to be built anyway). Ignored for output_format: 'pdf'.
    return_preview?: boolean
    // Same node-level logo (generate_deck_config.logo_url) used by /generate
    // and the real campaign-send path. Without this, Run Test's Download/
    // Render buttons silently build a deck with no logo.
    logo_url?: string
  }): Promise<AxiosResponse<{
    filename: string
    download_url: string
    output_format?: string
    preview_images?: string[]
    preview_aspect_ratio?: number | null
  }>> =>
    api.post('/presentations/render-slides', data, { timeout: 300000 }),

  /** Fetch a generated file as a Blob (uses auth headers). */
  fetchFileBlob: (downloadUrl: string): Promise<Blob> => {
    const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api').replace('/api', '')
    return api.get(`${baseUrl}${downloadUrl}`, { responseType: 'blob', timeout: 300000 }).then(r => r.data)
  },

  uploadLogo: (file: File): Promise<AxiosResponse<{ logo_path: string; logo_url: string; filename: string }>> => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/presentations/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
}

// Every write below is called from WarmupSection.tsx, which already renders
// its own toast with a specific message (getErrorMessage(err, ...) for
// connect, a per-action fallback string for start/pause/stop). Without
// skipErrorToast, the global axios interceptor (see the response interceptor
// above) ALSO fires a toast for the same error — e.g. connecting with an
// out-of-range target_daily_limit showed both the interceptor's generic
// "Request failed" and the component's specific message stacked on screen.
export const warmupApi = {
  connect: (senderEmailId: number, data: ConnectWarmupData) =>
    api.post<WarmupAccount>(`/sender-emails/${senderEmailId}/warmup`, data, { skipErrorToast: true } as any),

  getStatus: (senderEmailId: number) =>
    api.get<WarmupAccount | null>(`/sender-emails/${senderEmailId}/warmup`),

  start: (senderEmailId: number) =>
    api.post<WarmupAccount>(`/sender-emails/${senderEmailId}/warmup/start`, undefined, { skipErrorToast: true } as any),

  pause: (senderEmailId: number) =>
    api.post<WarmupAccount>(`/sender-emails/${senderEmailId}/warmup/pause`, undefined, { skipErrorToast: true } as any),

  /** Ask the provider for the live figures now, rather than reading whatever
   *  the 30-minute sweep last stored. Backs Refresh on the warmup card. */
  sync: (senderEmailId: number) =>
    api.post<WarmupAccount>(`/sender-emails/${senderEmailId}/warmup/sync`, undefined, { skipErrorToast: true } as any),

  // Rotate the provider key in place. The alternative was stop() then
  // connect(), which deletes the row and everything on it — the key,
  // warmup_started_at, the remote account's ramp progress (AB#22439).
  updateApiKey: (senderEmailId: number, data: UpdateWarmupApiKeyData) =>
    api.put<WarmupAccount>(`/sender-emails/${senderEmailId}/warmup/api-key`, data, { skipErrorToast: true } as any),

  stop: (senderEmailId: number) =>
    api.delete(`/sender-emails/${senderEmailId}/warmup`, { skipErrorToast: true } as any),

  // Connect via the provider's OWN OAuth (Instantly's Google/Microsoft consent
  // screen) instead of a raw SMTP/IMAP password — the only path for a Nylas-
  // brokered mailbox. init returns an auth_url to open in a new tab; poll
  // until status is no longer "pending".
  initOAuth: (senderEmailId: number, data: ConnectWarmupData) =>
    api.post<OAuthWarmupInitResult>(`/sender-emails/${senderEmailId}/warmup/oauth/init`, data, { skipErrorToast: true } as any),

  pollOAuth: (senderEmailId: number, oauthSessionId: number) =>
    api.get<OAuthWarmupPollResult>(`/sender-emails/${senderEmailId}/warmup/oauth/status/${oauthSessionId}`, { skipErrorToast: true } as any),
}

// Billing
export const billingApi = {
  // Plans
  getPlans: () => api.get('/billing/plans'),

  // Subscription
  getSubscription: () => api.get('/billing/subscription'),
  createCheckoutSession: (data: { plan_id: number; success_url: string; cancel_url: string }) =>
    api.post('/billing/checkout-session', data),
  changePlan: (new_plan_id: number) => api.post('/billing/change-plan', { new_plan_id }),
  cancelSubscription: () => api.post('/billing/cancel'),
  reactivateSubscription: () => api.post('/billing/reactivate'),

  // Plan-queue (stacking 30-day passes). `seats` is required for a per-seat
  // plan (Momentum: 2-5) and omitted for a single-seat one.
  switchPlan: (data: { plan_id: number; success_url: string; cancel_url: string; billing_cycle?: 'monthly' | 'annual'; seats?: number }) =>
    api.post('/billing/switch-plan', data),

  // Seats (Momentum per-seat spec §5, §7, §8)
  getSeats: () => api.get('/billing/seats', { skipErrorToast: true } as any),
  /** Buy more seats now — prorated and charged immediately by Stripe. */
  addSeats: (additional_seats: number) => api.post('/billing/seats/add', { additional_seats }),
  /** Pay for fewer seats from the next renewal (no mid-cycle refund). */
  reduceSeats: (seats: number) => api.post('/billing/seats/reduce', { seats }),
  cancelSeatReduction: () => api.delete('/billing/seats/reduce'),
  /** Every teammate's credit usage and wallet — Analyst + Owner/Admin see the
   *  whole team, Manager/Rep their own row (credit_budgets.view_usage). */
  // skipErrorToast: the endpoint is plan-gated (per_user_credit_budgets), so a
  // Starter account gets a 403 that is "nothing to show", not an error to
  // announce -- the store turns it into an empty table.
  getTeamCreditUsage: () => api.get('/credit-budgets/usage', { skipErrorToast: true } as any),

  // Credits
  getCredits: () => api.get('/billing/credits'),
  getCreditPacks: () => api.get('/billing/credit-packs'),
  purchaseCredits: (data: { credit_pack_id: number; success_url: string; cancel_url: string }) =>
    api.post('/billing/purchase-credits', data),
  createPaymentIntent: (credit_pack_id: number) =>
    api.post('/billing/create-payment-intent', { credit_pack_id }),

  // Transactions
  getTransactions: (params?: { skip?: number; limit?: number; action?: string; user_id?: number }) =>
    api.get('/billing/transactions', { params }),
  getTransactionSummary: () => api.get('/billing/transactions/summary'),

  // Usage analytics
  getUsageDaily: (days?: number) => api.get('/billing/usage/daily', { params: { days } }),
  getUsageBreakdown: () => api.get('/billing/usage/breakdown'),
  getUsageTrends: () => api.get('/billing/usage/trends'),

  // Estimator
  /** What the estimator can price, with live per-unit costs. */
  estimateCatalog: (byok = false): Promise<AxiosResponse<EstimatorCatalog>> =>
    api.get('/billing/estimate/catalog', { params: { byok } }),
  estimate: (data: EstimateRequestPayload): Promise<AxiosResponse<EstimateResponse>> =>
    api.post('/billing/estimate', data),
  estimateAction: (data: { action: string; input_size_hint: number }) =>
    api.post('/billing/estimate-action', data),

  // Settings
  getSettings: () => api.get('/billing/settings'),
  updateSettings: (data: Partial<{ auto_topup_enabled: boolean; auto_topup_credits: number | null; auto_topup_threshold: number | null }>) =>
    api.put('/billing/settings', data),

  // Payment method
  createSetupIntent: () => api.post('/billing/payment-method'),
  removePaymentMethod: () => api.delete('/billing/payment-method'),

  // Payment history — Stripe invoices for the account owner's customer.
  // `starting_after` is a Stripe cursor (the last invoice id on the page), not
  // an offset; the modal pages with it when `has_more` comes back true.
  getInvoices: (params?: { limit?: number; starting_after?: string }) =>
    api.get('/billing/invoices', { params }),

  // Portal
  createPortalSession: () => api.post('/billing/portal-session'),
}

// Tasks API endpoints
export const tasksApi = {
  getTasks: (params?: {
    status?: string
    priority?: string
    task_type?: string
    sort_by?: 'due_date' | 'created_at'
    sort_dir?: 'asc' | 'desc'
    limit?: number
    offset?: number
  }): Promise<AxiosResponse<any[]>> =>
    api.get('/tasks/', { params }),

  getStats: (): Promise<AxiosResponse<{
    total: number
    pending: number
    in_progress: number
    completed: number
    overdue: number
  }>> =>
    api.get('/tasks/stats'),

  updateTask: (
    id: number,
    data: { status?: string; priority?: string; title?: string; description?: string; assigned_to?: string }
  ): Promise<AxiosResponse<any>> =>
    api.patch(`/tasks/${id}`, data),
}

// Meetings API endpoints (Book Meeting feature)
export interface MeetingHost { email: string; name: string }
export interface CalendlyEventType { uri?: string; name?: string; duration?: number; scheduling_url?: string }
export interface MeetingSlot { start: string; end: string; label: string; scheduling_url?: string }
export interface Meeting {
  id: number
  contact_id: number
  host_email: string
  organizer_email?: string
  title: string
  description?: string
  provider: string
  start_time?: string
  end_time?: string
  duration_minutes: number
  timezone?: string
  attendees?: string[]
  status: string
  join_url?: string
  zoom_join_url?: string
  created_at?: string
}

export const meetingsApi = {
  getHosts: (): Promise<AxiosResponse<{ hosts: MeetingHost[]; calendly_event_types: CalendlyEventType[]; outlook_host?: string; zoom_host?: string }>> =>
    api.get('/meetings/hosts'),

  getAvailability: (params: {
    provider: string
    host: string
    date_from: string
    date_to: string
    duration: number
    timezone?: string
    attendees?: string
  }): Promise<AxiosResponse<MeetingSlot[]>> =>
    api.get('/meetings/availability', { params }),

  createMeeting: (data: {
    contact_id: number
    provider: string
    host: string
    title?: string
    description?: string
    start_time: string
    duration: number
    attendees?: string[]
    timezone?: string
    task_id?: number
  }): Promise<AxiosResponse<Meeting>> =>
    api.post('/meetings/', data),

  getMeetings: (params?: { contact_id?: number; status?: string }): Promise<AxiosResponse<Meeting[]>> =>
    api.get('/meetings/', { params }),

  updateMeeting: (
    id: number,
    data: { start_time?: string; duration?: number; title?: string; description?: string; attendees?: string[] }
  ): Promise<AxiosResponse<Meeting>> =>
    api.patch(`/meetings/${id}`, data),

  cancelMeeting: (id: number): Promise<AxiosResponse<Meeting>> =>
    api.delete(`/meetings/${id}`),

  sendCalendlyLink: (data: { contact_id: number; scheduling_url: string }): Promise<AxiosResponse<{ success: boolean }>> =>
    api.post('/meetings/send-calendly-link', data),

  sendFollowUp: (id: number): Promise<AxiosResponse<{ success: boolean }>> =>
    api.post(`/meetings/${id}/follow-up`),
}

// Mail Structure Templates API endpoints
export const mailTemplatesApi = {
  getAll: (): Promise<AxiosResponse<TemplateListItem[]>> =>
    api.get('/mail-templates'),
  // `silent` suppresses the interceptor's error toast. The workflow builder
  // resolves a node's stored template_id only to show block tooltips, and a
  // template that has since been deleted (or belongs to a workflow authored by
  // someone else) must not red-toast "Template not found" at someone who did
  // nothing but open a Send Email node. The Templates page omits it and keeps
  // the toast, where the failure IS the thing the user asked for.
  getById: (id: number, opts?: { silent?: boolean }): Promise<AxiosResponse<MailStructureTemplate>> =>
    api.get(`/mail-templates/${id}`, opts?.silent ? ({ skipErrorToast: true } as any) : undefined),
  create: (data: TemplateCreateRequest): Promise<AxiosResponse<MailStructureTemplate>> =>
    api.post('/mail-templates', data),
  update: (id: number, data: TemplateUpdateRequest): Promise<AxiosResponse<MailStructureTemplate>> =>
    api.patch(`/mail-templates/${id}`, data),
  delete: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/mail-templates/${id}`),
  duplicate: (id: number): Promise<AxiosResponse<MailStructureTemplate>> =>
    api.post(`/mail-templates/${id}/duplicate`),
  setDefault: (id: number): Promise<AxiosResponse<MailStructureTemplate>> =>
    api.patch(`/mail-templates/${id}/set-default`),
}

// Feedback & Support API — multipart because the attachment rides in the
// same request as the rest of the form (mirrors presentationsApi.uploadLogo
// and materialsApi's upload). FeedbackSupportSection.tsx renders its own
// inline error banner per the design, so this opts out of the global axios
// interceptor's toast the same way warmupApi does (skipErrorToast) --
// otherwise a validation 422 would show both the interceptor's generic toast
// and the form's own inline message.
export const feedbackApi = {
  submit: (formData: FormData): Promise<AxiosResponse<FeedbackSubmitResult>> =>
    api.post('/feedback/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      skipErrorToast: true,
    } as any),
  /** The caller's own submissions with the simplified, user-facing status, and how many moved since they last looked. */
  mine: (): Promise<AxiosResponse<FeedbackMineResponse>> =>
    api.get('/feedback/mine', { skipErrorToast: true } as any),
  /** The reporter opened the Reports card: clear the unseen count. */
  markReportsSeen: (): Promise<AxiosResponse<FeedbackSeenResponse>> =>
    api.post('/feedback/mine/seen', null, { skipErrorToast: true } as any),
}

export default api